/**
 * Cognito Hosted UI 登录（Authorization Code + PKCE）。
 *
 * **为什么直接实现 PKCE 而不用 aws-amplify：**
 *
 * Squawk 用独立品牌域名，无法复用 Pass 那套 `.lookatwallstreet.com` 的
 * cookie 域 SSO，所以必然走 hosted UI 重定向（与 Nexus 的 nexusquant.co 同构）。
 * 走 hosted UI 之后，Amplify 的主要价值（内置登录 UI、会话管理）基本用不上，
 * 剩下的是 200KB+ 的运行时和一层不透明的会话状态 —— Pass 为此踩过
 * 「冷启动时 No current user」和自定义 CookieStorage 的坑。
 *
 * PKCE 本身约 120 行、行为完全可预期、可测试，且没有依赖会在升级时改行为。
 *
 * **令牌存储**：access/id token 只放**内存**，refresh token 放 sessionStorage。
 * 不用 localStorage —— 它对 XSS 完全敞开且跨标签页长期驻留。
 * sessionStorage 限定在单个标签页会话，关掉即失效，是这里的合理折中。
 */

const AUTH_DOMAIN =
  import.meta.env.VITE_COGNITO_DOMAIN ?? 'https://auth.lookatwallstreet.com';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
// ⚠️ 必须带 /console 前缀，且**必须与 Cognito app client 里登记的回调 URL
//    逐字节一致** —— 不一致时 Cognito 直接拒绝授权请求，错误停在它自己的
//    页面上，我们这边一行日志都不会有。
const REDIRECT_URI =
  import.meta.env.VITE_REDIRECT_URI ?? `${location.origin}/console/callback`;
const SCOPES = 'openid email profile';

const VERIFIER_KEY = 'squawk_pkce_verifier';
const STATE_KEY = 'squawk_oauth_state';
const REFRESH_KEY = 'squawk_refresh_token';

interface Tokens {
  idToken: string;
  accessToken: string;
  expiresAt: number;
}

/** 内存中的令牌。刷新页面即丢失，靠 refresh token 重新换取。 */
let tokens: Tokens | null = null;
/** 并发请求共享同一次刷新，避免同时发起多个 refresh 把 token 互相作废。 */
let refreshInFlight: Promise<Tokens | null> | null = null;

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function sha256(text: string): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return new Uint8Array(digest);
}

function randomString(bytes = 32): string {
  return base64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

/** 跳转到 Cognito Hosted UI 开始登录。 */
export async function login(): Promise<void> {
  if (!CLIENT_ID) {
    throw new Error('未配置 VITE_COGNITO_CLIENT_ID');
  }
  const verifier = randomString(48);
  const state = randomString(16);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: SCOPES,
    state,
    code_challenge: base64url(await sha256(verifier)),
    code_challenge_method: 'S256',
  });
  location.assign(`${AUTH_DOMAIN}/oauth2/authorize?${params}`);
}

/**
 * 同一次页面加载内正在进行的回调处理。
 *
 * ⚠️ **回调处理必须幂等。** `<StrictMode>` 在开发环境会把 effect 跑两次：
 * 第一次读走并删掉 state/verifier，第二次读到 null 就抛「登录回调校验失败」——
 * 本地登录看起来永远是坏的，而真正的原因跟登录毫无关系。
 *
 * 生产构建不会双调，但**授权码本身是一次性的**：用户刷新 /callback 页面、
 * 或浏览器重试请求，第二次兑换一定会被 Cognito 拒绝。共享同一个飞行中的
 * Promise 之后，重复调用会拿到同一个结果，而不是各自去兑换一次。
 * 这与下面 `refreshInFlight` 是同一个模式。
 */
let callbackInFlight: Promise<void> | null = null;

/** 处理 Hosted UI 回调，用 code 换取令牌。**同一次页面加载内可安全重复调用。** */
export async function handleCallback(search: string): Promise<void> {
  callbackInFlight ??= exchangeCode(search).finally(() => {
    // 失败时清空，让用户点「重新登录」后能再走一遍完整流程。
    callbackInFlight = null;
  });
  return callbackInFlight;
}

async function exchangeCode(search: string): Promise<void> {
  const params = new URLSearchParams(search);
  const error = params.get('error');
  if (error) {
    throw new Error(`${error}: ${params.get('error_description') ?? ''}`);
  }

  const code = params.get('code');
  const state = params.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  // state 校验是 CSRF 防线，不能因为「反正拿到 code 了」就跳过。
  if (!code || !state || !expectedState || state !== expectedState || !verifier) {
    throw new Error('登录回调校验失败，请重新登录');
  }
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  const res = await fetch(`${AUTH_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: verifier,
    }),
  });
  if (!res.ok) {
    throw new Error(`换取令牌失败：${res.status}`);
  }
  storeTokens(await res.json());
}

function storeTokens(payload: {
  id_token: string;
  access_token: string;
  expires_in: number;
  refresh_token?: string;
}): Tokens {
  tokens = {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    // 提前 60 秒视为过期，避免「刚好在请求飞行途中失效」。
    expiresAt: Date.now() + (payload.expires_in - 60) * 1000,
  };
  if (payload.refresh_token) {
    sessionStorage.setItem(REFRESH_KEY, payload.refresh_token);
  }
  return tokens;
}

async function refresh(): Promise<Tokens | null> {
  const token = sessionStorage.getItem(REFRESH_KEY);
  if (!token) return null;

  const res = await fetch(`${AUTH_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: token,
    }),
  });
  if (!res.ok) {
    // refresh token 也失效了 —— 清干净并让调用方引导重新登录。
    sessionStorage.removeItem(REFRESH_KEY);
    return null;
  }
  return storeTokens(await res.json());
}

/** 取当前 ID token；必要时自动刷新。未登录返回 null。 */
export async function getIdToken(): Promise<string | null> {
  if (tokens && Date.now() < tokens.expiresAt) {
    return tokens.idToken;
  }
  // 多个并发请求共享同一次刷新 —— 否则它们会互相把 refresh token 作废。
  // 网络异常时返回 null 而不是把异常抛出去：调用方据此引导重新登录，
  // 否则界面上会出现一个「TypeError: Failed to fetch」，看不出与登录有关。
  refreshInFlight ??= refresh()
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });
  return (await refreshInFlight)?.idToken ?? null;
}

export function isLoggedIn(): boolean {
  return tokens !== null || sessionStorage.getItem(REFRESH_KEY) !== null;
}

export function logout(): void {
  // ⚠️ 清掉本地存储**不等于**令牌失效。Cognito 的 /logout 只结束 hosted UI
  //    会话，不会作废 refresh token —— 一个被 XSS 偷走的 refresh token
  //    在「用户已经退出登录」之后仍可继续换取新的 id token。
  //    revoke 是尽力而为：网络失败也不该挡住退出流程。
  const refreshToken = sessionStorage.getItem(REFRESH_KEY);
  if (refreshToken && CLIENT_ID) {
    void fetch(`${AUTH_DOMAIN}/oauth2/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: refreshToken, client_id: CLIENT_ID }),
      keepalive: true,   // 页面马上要跳走，keepalive 才能让请求发出去
    }).catch(() => {
      /* 撤销失败不影响退出；令牌仍会在 Cognito 侧到期 */
    });
  }

  tokens = null;
  callbackInFlight = null;
  sessionStorage.removeItem(REFRESH_KEY);
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: `${location.origin}/`,
  });
  location.assign(`${AUTH_DOMAIN}/logout?${params}`);
}
